/**
 * Animated canvas blob shown during audio resync progress.
 * Used by uc-edit-bar.vue (player) and AuthorEditorLite.vue (author).
 *
 * @param canvas   The canvas element to draw into.
 * @param shouldStop  Called each frame — return true to stop the animation.
 * @returns A cleanup function that removes event listeners immediately.
 */
export function runAudioProgressAnimation(
  canvas: HTMLCanvasElement,
  shouldStop: () => boolean
): () => void {
  const ctx = canvas.getContext('2d');
  const pi = Math.PI;
  const points = 10;
  const radius = 55;
  const w = 175;
  const h = 175;
  const center = { x: w / 2, y: h / 2 };
  const rangeMin = 1;
  const rangeMax = 12;

  canvas.width = w;
  canvas.height = h;
  canvas.style.width = w + 'px';
  canvas.style.height = h + 'px';

  let mouseY = 0;
  let tick = 0;

  const handleMove = (e: MouseEvent) => {
    mouseY = e.clientY;
  };
  window.addEventListener('mousemove', handleMove, true);

  const cleanup = () =>
    window.removeEventListener('mousemove', handleMove, true);

  const random = (a: number, b: number) =>
    Math.floor(Math.random() * (Math.max(a, b) - Math.min(a, b) + 1)) +
    Math.min(a, b);
  const cycle = (n: number, len: number) => ((n % len) + len) % len;

  let gradients: CanvasGradient[] = [];
  if (ctx) {
    const g = (c0: string, c1: string) => {
      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, c0);
      grad.addColorStop(1, c1);
      return grad;
    };
    gradients = [
      g('#6861E9', '#4C20EF'),
      g('#6861E9', '#4C20EF'),
      g('#A5B4FC', '#6366F1'),
      g('#F2F3FD', '#A5B4FC')
    ];
  }

  type SwingPoint = {
    x: number;
    y: number;
    radian: number;
    range: number;
    phase: number;
  };

  const circles: SwingPoint[][] = gradients.map(() =>
    Array.from({ length: points }, (_, i) => {
      const radian = ((pi * 2) / points) * i;
      return {
        x: center.x + radius * Math.cos(radian),
        y: center.y + radius * Math.sin(radian),
        radian,
        range: random(rangeMin, rangeMax),
        phase: 0
      };
    })
  );

  const drawCurve = (pts: SwingPoint[], fill: CanvasGradient) => {
    if (!ctx) return;
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(
      (pts[cycle(-1, points)].x + pts[0].x) / 2,
      (pts[cycle(-1, points)].y + pts[0].y) / 2
    );
    for (let i = 0; i < pts.length; i++) {
      ctx.quadraticCurveTo(
        pts[i].x,
        pts[i].y,
        (pts[i].x + pts[cycle(i + 1, points)].x) / 2,
        (pts[i].y + pts[cycle(i + 1, points)].y) / 2
      );
    }
    ctx.closePath();
    ctx.fill();
  };

  const swingCircle = () => {
    if (shouldStop()) {
      cleanup();
      return;
    }
    if (ctx) {
      ctx.clearRect(0, 0, w, h);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'screen';
    }
    const phase = mouseY !== 0 ? mouseY / 500 + 1 : 3 * Math.sin(tick / 65);
    for (let k = 0; k < circles.length; k++) {
      const pts = circles[k];
      for (let i = 0; i < pts.length; i++) {
        pts[i].phase += random(1, 10) * -0.01;
        const r =
          radius + pts[i].range * phase * Math.sin(pts[i].phase) - rangeMax;
        pts[i].radian += pi / 360;
        pts[i].x = center.x + r * Math.cos(pts[i].radian);
        pts[i].y = center.y + r * Math.sin(pts[i].radian);
      }
      drawCurve(pts, gradients[k]);
    }
    tick++;
    requestAnimationFrame(swingCircle);
  };

  requestAnimationFrame(swingCircle);

  return cleanup;
}
